import * as React from 'react';

import type {DragStartEvent, DragOverEvent, DragEndEvent} from '@dnd-kit/react';
import {isSortable} from '@dnd-kit/dom/sortable';

import type {DropIndicatorOrientation} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/drop-indicator';
import {
  NEW_ROW_PREFIX,
  ROW_END_SUFFIX,
} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/utils/handle-drag-end';


export type IndicatorTarget = {
  droppableId: string;
  index: number;
  orientation: DropIndicatorOrientation;
};

export type UseDragIndicatorReturn = {
  indicatorTarget: IndicatorTarget | null;
  source: {droppableId: string; index: number} | null;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
};

type Handlers = {
  onDragStart?: (event: DragStartEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragEnd?: (event: DragEndEvent) => void;
};

function getDroppableId(
  droppable: DragOverEvent['operation']['target'],
): string {
  if (!droppable) {
    return '';
  }
  if (isSortable(droppable)) {
    return String(droppable.sortable.group ?? droppable.id);
  }
  const id = String(droppable.id);
  // Row-end sentinel: strip the __end suffix so the indicator targets the row.
  if (id.endsWith(ROW_END_SUFFIX)) {
    return id.slice(0, -ROW_END_SUFFIX.length);
  }
  return id;
}

function getDroppableIndex(
  droppable: DragOverEvent['operation']['target'],
): number {
  if (!droppable) {
    return 0;
  }
  if (isSortable(droppable)) {
    return droppable.sortable.index;
  }
  const id = String(droppable.id);
  // Row-end sentinel: return a large index so the indicator renders at the
  // right edge of the last tile (tile-row handles idx >= children.length).
  if (id.endsWith(ROW_END_SUFFIX)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return 0;
}

export function useDragIndicator({
  onDragStart,
  onDragOver,
  onDragEnd,
}: Handlers = {}): UseDragIndicatorReturn {
  const [indicatorTarget, setIndicatorTarget] =
    React.useState<IndicatorTarget | null>(null);
  const [source, setSource] = React.useState<{
    droppableId: string;
    index: number;
  } | null>(null);
  const sourceRef = React.useRef<{droppableId: string; index: number} | null>(
    null,
  );

  const handleDragStart = React.useCallback(
    (event: DragStartEvent) => {
      const src = event.operation.source;
      const srcInfo = src
        ? {
            droppableId: isSortable(src)
              ? String(src.sortable.group ?? src.id)
              : String(src.id),
            index: isSortable(src) ? src.sortable.index : 0,
          }
        : null;
      sourceRef.current = srcInfo;
      setSource(srcInfo);
      if (typeof document !== 'undefined') {
        document.body.dataset.dragging = 'true';
      }
      onDragStart?.(event);
    },
    [onDragStart],
  );

  const handleDragOver = React.useCallback(
    (event: DragOverEvent) => {
      const target = event.operation.target;
      const src = sourceRef.current;
      if (!target || !src) {
        setIndicatorTarget(null);
        onDragOver?.(event);
        return;
      }
      const destDroppableId = getDroppableId(target);
      const destIndex = getDroppableIndex(target);
      if (destDroppableId === src.droppableId && destIndex === src.index) {
        setIndicatorTarget(null);
        onDragOver?.(event);
        return;
      }
      const orientation: DropIndicatorOrientation = destDroppableId.startsWith(
        NEW_ROW_PREFIX,
      )
        ? 'horizontal'
        : 'vertical';
      setIndicatorTarget((prev) => {
        if (
          prev &&
          prev.droppableId === destDroppableId &&
          prev.index === destIndex &&
          prev.orientation === orientation
        ) {
          return prev;
        }
        return {
          droppableId: destDroppableId,
          index: destIndex,
          orientation,
        };
      });
      onDragOver?.(event);
    },
    [onDragOver],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      sourceRef.current = null;
      setSource(null);
      setIndicatorTarget(null);
      if (typeof document !== 'undefined') {
        delete document.body.dataset.dragging;
      }
      onDragEnd?.(event);
    },
    [onDragEnd],
  );

  // Clean up body dataset if unmount happens mid-drag (e.g. error boundary,
  // navigation). Without this, data-dragging persists permanently.
  React.useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        delete document.body.dataset.dragging;
      }
    };
  }, []);

  return {
    indicatorTarget,
    source,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  };
}
